// src/store/reducers/leaderboardSlice.ts

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchApi } from '../../utilities/api';
import { ENDPOINTS } from '../../utilities/endpoints';

//
// 1) Define the shape of one leaderboard entry
//
export interface LeaderboardEntry {
  _id: string;
  fullName: string;
  userName: string;
  totalPropertyPrice: number;
  numberOfProperties: number;
  rank: number;
}

//
// 2) The slice’s state
//
interface LeaderboardState {
  isLoading: boolean;
  error: string | null;
  rankedResponse: LeaderboardEntry[];
  totalCount: number;
}

const initialState: LeaderboardState = {
  isLoading:       false,
  error:           null,
  rankedResponse:  [],
  totalCount:      0,
};

//
// 3) Thunk: fetchLeaderboard
//
export const fetchLeaderboard = createAsyncThunk<
  // returned data
  { rankedResponse: LeaderboardEntry[]; totalCount: number },
  // you can pass in optional query params here
  { sortBy?: string; name?: string },
  { rejectValue: string }
>(
  'leaderboard/fetch',
  async ({ sortBy, name }, { rejectWithValue }) => {
    try {
      const jwt = localStorage.getItem('token');
      if (!jwt) throw new Error('No auth token found');

      // build URL with optional query params
      const url = new URL(ENDPOINTS.PROPERTIES.LEADERBOARD);
      if (sortBy) url.searchParams.append('sortBy', sortBy);
      if (name)   url.searchParams.append('name',   name);

      const res = await fetchApi<{
        rankedResponse: LeaderboardEntry[];
        totalCount:     number;
      }>(
        url.toString(),
        {
          method: 'GET',
          headers: { Authorization: jwt },
        }
      );

      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch leaderboard');
    }
  }
);

//
// 4) Create the slice
//
const leaderboardSlice = createSlice({
  name: 'leaderboard',
  initialState,
  reducers: {
    /** optional action to reset state */
    resetLeaderboardState(state) {
      state.isLoading      = false;
      state.error          = null;
      state.rankedResponse = [];
      state.totalCount     = 0;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchLeaderboard.pending, state => {
        state.isLoading = true;
        state.error     = null;
      })
      .addCase(
        fetchLeaderboard.fulfilled,
        (state, action: PayloadAction<{ rankedResponse: LeaderboardEntry[]; totalCount: number }>) => {
          state.isLoading      = false;
          state.rankedResponse = action.payload.rankedResponse;
          state.totalCount     = action.payload.totalCount;
        }
      )
      .addCase(fetchLeaderboard.rejected, (state, action) => {
        state.isLoading = false;
        state.error     = action.payload ?? action.error.message ?? 'Unknown error';
      });
  },
});

export const { resetLeaderboardState } = leaderboardSlice.actions;
export default leaderboardSlice.reducer;
